// 由 scripts/sync-kbs.mjs 自动生成，请勿手改；源文件：supabase/functions/_shared/kbs/registry.ts
// KBS 流水线步骤注册表（032 §2 · 单一权威源）
// 消灭 6 处硬编码：state.ts / narrate.ts / Workspace STAGES / summarize / PreviewBody / DB check
// 各处一律从 KBS_STEPS 派生；改步骤只改这里。
// KBS_DEPS 依赖声明以 034 §1.2 为唯一权威（与 032 §3.3 有差异时以本文为准）。

export type StepLine = "K" | "B" | "S" | "SYS";

export interface StepDef {
  /** job status & job_steps.step，如 "k1" */
  key: string;
  line: StepLine;
  /** LLM 路由键（llm/config.ts ROUTES） */
  agent: string;
  /** 中文名 */
  label: string;
  /** 展示编号，如 "K1" */
  code: string;
  icon: string;
  progress: number;
  /** 步骤开场叙事（narrateStart / 前端实时流用） */
  narrativeStart: string;
  /** 是否需要联网搜索（K1/K2 为 true） */
  usesSearch?: boolean;
  /** 该工具预计贡献页数（outline 组装用，030_Spec §4） */
  pptPages: number;
}

/** KBS v2 流水线（058 T10 / 057 §3.1）：
 *  intake → E1 证据核对 → K1-K3 → E2(strategy 反证) → K4 → B1-B5 → S1-S3 → M1 执行计划
 *  → financial 确定性核算 → S4 创意简报（锁定版本引用）→ E2(execution 审查)
 *  → outline → drafting → review → artwork → assemble
 *  顺序变化的理由：S4 必须引用已核算 KPI 与执行计划；E2 两阶段是同一 Agent 的 stage 参数；
 *  E1/M1 不产页面（pptPages 0），产物经 brief 注入下游。 */
export const KBS_STEPS: StepDef[] = [
  { key: "intake", line: "SYS", agent: "intake", label: "资料解析", code: "IN", icon: "📄", progress: 4, narrativeStart: "开始解析品牌资料，抽取事实、产品线与目标人群…", pptPages: 1 },
  { key: "e1", line: "SYS", agent: "kbs-e1", label: "证据整理核对", code: "E1", icon: "🧾", progress: 8, narrativeStart: "核对主张与证据是否匹配，标记目标/假设/未知…", pptPages: 0 },
  { key: "k1", line: "K", agent: "kbs-k1", label: "品类地图扫描", code: "K1", icon: "🗺️", progress: 12, usesSearch: true, narrativeStart: "正在执行三层品类扫描：直接竞品、相邻品类、消费者认知…", pptPages: 2 },
  { key: "k2", line: "K", agent: "kbs-k2", label: "消费者洞察挖掘", code: "K2", icon: "🔍", progress: 19, usesSearch: true, narrativeStart: "用五层剥离法挖掘三类人群的隐性渴望…", pptPages: 2 },
  { key: "k3", line: "K", agent: "kbs-k3", label: "机会空间定义", code: "K3", icon: "⭐", progress: 26, narrativeStart: "构造机会评分矩阵，锁定首选方向与北极星语句…", pptPages: 1 },
  { key: "e2s", line: "SYS", agent: "kbs-e2", label: "策略反证审查", code: "E2s", icon: "🧪", progress: 31, narrativeStart: "挑战关键前提：需求/供给/能力证据与矩阵评分是否支持首选机会…", pptPages: 0 },
  { key: "k4", line: "K", agent: "kbs-k4", label: "洞察报告汇总", code: "K4", icon: "📑", progress: 34, narrativeStart: "汇总 K 线洞察，产出执行摘要与下一步建议…", pptPages: 1 },
  { key: "b1", line: "B", agent: "kbs-b1", label: "品牌定位罗盘", code: "B1", icon: "🧭", progress: 40, narrativeStart: "围绕北极星定义四维定位：参照系/人群/承诺/信任状…", pptPages: 1 },
  { key: "b2", line: "B", agent: "kbs-b2", label: "价值主张金字塔", code: "B2", icon: "🔺", progress: 45, narrativeStart: "三步提炼价值主张，搭建四层金字塔…", pptPages: 1 },
  { key: "b3", line: "B", agent: "kbs-b3", label: "品牌故事构建", code: "B3", icon: "📖", progress: 49, narrativeStart: "用五幕叙事框架构建品牌故事…", pptPages: 1 },
  { key: "b4", line: "B", agent: "kbs-b4", label: "品牌架构决策", code: "B4", icon: "🏗️", progress: 52, narrativeStart: "走品牌架构三问决策树…", pptPages: 1 },
  { key: "b5", line: "B", agent: "kbs-b5", label: "策略蓝皮书汇总", code: "B5", icon: "📘", progress: 56, narrativeStart: "收束 B 线：语言规范、实施优先级与 KPI…", pptPages: 1 },
  { key: "s1", line: "S", agent: "kbs-s1", label: "Big Idea 创意", code: "S1", icon: "💡", progress: 62, narrativeStart: "张力碰撞：消费者渴望 × 品牌能力 = Big Idea…", pptPages: 2 },
  { key: "s2", line: "S", agent: "kbs-s2", label: "传播策略框架", code: "S2", icon: "📡", progress: 66, narrativeStart: "构建品牌声音、传播节奏与渠道信息矩阵…", pptPages: 2 },
  { key: "s3", line: "S", agent: "kbs-s3", label: "内容矩阵规划", code: "S3", icon: "🗓️", progress: 70, narrativeStart: "规划内容矩阵，解决每天发什么…", pptPages: 1 },
  { key: "m1", line: "SYS", agent: "kbs-m1", label: "执行计划配置", code: "M1", icon: "🧩", progress: 74, narrativeStart: "把创意展开成战术机制、资源责任与 KPI…", pptPages: 0 },
  { key: "financial", line: "SYS", agent: "financial", label: "经营测算(附录)", code: "FA", icon: "🧮", progress: 78, narrativeStart: "搭建经营测算模型（假设链→确定性核算）…", pptPages: 2 },
  { key: "s4", line: "S", agent: "kbs-s4", label: "创意简报", code: "S4", icon: "📋", progress: 82, narrativeStart: "把策略翻译成 10 字段创意简报（引用锁定版本）…", pptPages: 1 },
  { key: "e2e", line: "SYS", agent: "kbs-e2", label: "执行审查", code: "E2e", icon: "🧪", progress: 85, narrativeStart: "审查障碍→战术→机制→预算→KPI 是否逐环闭合…", pptPages: 0 },
  { key: "outline", line: "SYS", agent: "strategy", label: "PPT 大纲", code: "OL", icon: "🗒️", progress: 90, narrativeStart: "按 23 页骨架组装大纲，编排节奏与版式…", pptPages: 0 },
  { key: "drafting", line: "SYS", agent: "copy", label: "文案撰写", code: "DR", icon: "✍️", progress: 94, narrativeStart: "分批撰写各页文案…", pptPages: 0 },
  { key: "review", line: "SYS", agent: "critic", label: "评审打磨", code: "RV", icon: "🔎", progress: 96, narrativeStart: "Lint 机器校验 + 多维评审 + KBS 一致性检查…", pptPages: 0 },
  { key: "artwork", line: "SYS", agent: "art", label: "AI 配图", code: "AW", icon: "🎨", progress: 98, narrativeStart: "按品牌调性生成配图：文生图主视觉 → 图生图保持系列感…", pptPages: 0 },
  { key: "assemble", line: "SYS", agent: "builder", label: "装配出片", code: "AS", icon: "📦", progress: 99, narrativeStart: "装配整册 PPT，渲染版式与主题皮肤…", pptPages: 0 },
];

/** legacy 8 步流水线（pipeline==='legacy' 的历史 job 沿用；research/strategy 未入注册表） */
export const LEGACY_FLOW = ["intake", "research", "strategy", "financial", "outline", "drafting", "review", "assemble"] as const;

/**
 * 依赖声明（034 §1.2 唯一权威）：每个 Agent 只读它声明依赖的上游产物。
 * buildContext 按此从 job_steps 拉取；outline 的 ["*"] 表示读全部工具产物（经 mapper/brief）。
 */
export const KBS_DEPS: Record<string, string[]> = {
  e1: ["intake"],
  k1: ["intake", "e1"],
  k2: ["intake", "k1", "e1"],
  k3: ["k1", "k2"],
  e2s: ["k1", "k2", "k3"],
  k4: ["k1", "k2", "k3", "e2s"],
  b1: ["intake", "k2", "k3"],
  b2: ["b1", "intake"],
  b3: ["b1", "b2"],
  b4: ["intake", "b1"],
  b5: ["b1", "b2", "b3", "b4"],
  s1: ["k2", "b1", "b2"],
  s2: ["b5", "s1"],
  s3: ["s2", "s1"],
  // 058 T10（057 §3.1）：M1 把创意展开成战术先于财务核算；S4 引用锁定版本（M1+financial）
  m1: ["s1", "s2", "s3", "b5", "k2"],
  financial: ["intake", "b5", "s2", "m1"],
  s4: ["s1", "s2", "s3", "k2", "m1", "financial"],
  e2e: ["s4", "m1", "financial", "k3"],
  outline: ["*"],
  // T4.3：drafting 需要全部工具产物（各批 brief）+ financial（TAIL 批附录页）
  drafting: ["*"],
};

export const KBS_STEP_KEYS = KBS_STEPS.map((s) => s.key);

export function kbsStep(key: string): StepDef | undefined {
  return KBS_STEPS.find((s) => s.key === key);
}

/** KBS 流水线业务推进顺序（state.ts STATUS_FLOW 的来源） */
export const KBS_FLOW: string[] = KBS_STEP_KEYS;
